import { ReactNode, useMemo } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import Animated, { useAnimatedProps } from 'react-native-reanimated';
import Svg, { Circle } from 'react-native-svg';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

type Props = {
  size: number;
  stroke: string;
  track?: string;
  strokeWidth: number;
  radius?: number;
  progress: Animated.DerivedValue<number>;
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
};

export const CircularProgress = ({
  progress,
  size,
  stroke,
  strokeWidth,
  track,
  style,
  children,
  ...props
}: Props) => {
  const radius = props.radius ?? (size - strokeWidth * 2) / 2;
  const c = radius * 2 * Math.PI;
  const animatedProps = useAnimatedProps(() => {
    const p = progress.value;
    return {
      strokeDashoffset: (1 - p) * c,
    };
  });
  const svgStyle = useMemo<StyleProp<ViewStyle>>(
    () => ({
      transform: [{ rotate: '-90deg' }],
    }),
    [],
  );
  return (
    <Animated.View
      css={`
        width: ${size}px;
        height: ${size}px;
        flex-grow: 0;
        flex-shrink: 0;
      `}
      style={style}
    >
      {size > 0 && (
        <Svg width={size} height={size} style={svgStyle} fill="none">
          <AnimatedCircle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={c}
            animatedProps={animatedProps}
            strokeLinecap="round"
          />
          {track != null && (
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={track}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          )}
        </Svg>
      )}
      <>{children}</>
    </Animated.View>
  );
};
